import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OperationSubtitle from './OperationSubtitle';
import QuickAccessContent from './QuickAccessContent';
import OperationContext from './context';
import Context from '../../App/MainProvider/context';
import Section from '../../../shared/components/Section';
import OperationTabsContainer from './OperationTabsContainer';
import { PageHeader } from '../../../shared/components';
import Timeline from './Timeline';
import useQuery from '../../../shared/hooks/useQuery';
import SectionCollapsable from '../../../shared/components/SectionCollapsable';
import GeneralInformationTabContainer from './GeneralInformationTabContainer';
import EsrrSprTimeline from './EsrrSprTimeline';

const OperationHome: React.FC = () => {
  const { operation, loading } = useContext(OperationContext);
  const { notifyError } = useContext(Context);
  const { t } = useTranslation();
  const [timelineWidth, setTimelineWidth] = useState(0);

  const queryParams = useQuery();
  const from = queryParams.get('from') || undefined;
  const tab = queryParams.get('tab');
  const title = `Operation ${operation.number}`;
  const taskTitle = `About ${title}`;

  const onWidthset = (width: number) => {
    setTimelineWidth(width);
  };

  const linkConvergence = (
    <a
      target="_blank"
      href={`${process.env.REACT_APP_CONVERGENCE_URL}Operation/${operation.number}`}
      rel="noopener noreferrer"
      title={t('seeInConvergence')}
    >
      <FontAwesomeIcon icon="external-link-alt" />
    </a>
  );

  // notifyError() actualiza el estado de MainProvider (un componente ancestro,
  // no éste) - llamarlo directo en el cuerpo del render, como estaba antes,
  // es actualizar un componente distinto mientras éste se está renderizando.
  // React 18 lo toleraba en la práctica; React 19 lo corta con "Cannot update
  // a component while rendering a different component" y aborta el render
  // completo (activa el ErrorBoundary de react-router, dejando colgadas las
  // llamadas a la API que dependían de que este árbol terminara de montar) -
  // encontrado en QA manual con una operación real (AR-L1006) que tiene
  // invalidAssignments=true. No dependemos de `notifyError` en el array de
  // deps a propósito: no está memoizado en MainProvider (nueva referencia en
  // cada uno de sus renders), así que listarlo dispararía el efecto de nuevo
  // cada vez que notify() cambia el estado de MainProvider - un loop.
  useEffect(() => {
    if (operation.invalidAssignments) {
      const error: Error = { message: t('inconsistentAssignments'), name: '' };
      notifyError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation.invalidAssignments, operation.number]);

  return (
    <Container className="dashboard">
      <PageHeader title={title} link={linkConvergence} backUrl={from}>
        <OperationSubtitle />
      </PageHeader>
      <Row>
        <Section>
          <GeneralInformationTabContainer operation={operation} loading={loading} />
        </Section>
        <SectionCollapsable title="Quick Access" onlyMobile>
          <QuickAccessContent />
        </SectionCollapsable>
        <Section title="Operation Timeline" className="timeline-section">
          <Timeline operationId={operation.id} onWidthset={onWidthset} />
          <EsrrSprTimeline operationId={operation.id} timelineWidth={timelineWidth} />
        </Section>
        <Section title={taskTitle} md={9}>
          <OperationTabsContainer tab={tab ? Number.parseInt(tab, 10) : 1} />
        </Section>
        <Section title="Quick Access" md={3} onlyDesktop>
          <QuickAccessContent />
        </Section>
      </Row>
    </Container>
  );
};

export default OperationHome;
